import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private apiUrl = `${environment.apiUrl}/Auth/Login`;

    // Auth state — initialized safely without calling logout (Router not ready yet)
    isAuthenticated = signal<boolean>(this.checkTokenExists());
    currentUser = signal<{ name: string, email: string, role: string } | null>(this.getUserFromStorage());

    private http = inject(HttpClient);
    private router = inject(Router);

    constructor() { }

    login(email: string, password: string): Observable<any> {
        return this.http.post<any>(this.apiUrl, { email, password }).pipe(
            tap(response => this.setSession(response))
        );
    }

    private setSession(authResult: any) {
        localStorage.setItem('accessToken', authResult.accessToken);
        localStorage.setItem('expiresIn', authResult.expiresIn);
        this.isAuthenticated.set(true);
        this.currentUser.set(this.decodeToken(authResult.accessToken));
    }

    logout() {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('expiresIn');
        this.isAuthenticated.set(false);
        this.currentUser.set(null);
        this.router.navigate(['/login']);
    }

    /**
     * Safe check for signal initialization — only checks if a token
     * exists AND is decodable. Does NOT call logout() or router.navigate()
     * because those aren't safe during field initialization.
     */
    private checkTokenExists(): boolean {
        const token = localStorage.getItem('accessToken');
        if (!token) return false;

        // Verify the token can be decoded
        const user = this.decodeToken(token);
        if (!user) {
            // Token is corrupted — silently clear it (no Router calls here)
            localStorage.removeItem('accessToken');
            localStorage.removeItem('expiresIn');
            return false;
        }

        // Check expiration
        if (this.isTokenExpired(token)) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('expiresIn');
            return false;
        }

        return true;
    }

    getToken(): string | null {
        const token = localStorage.getItem('accessToken');
        if (!token) return null;

        if (this.isTokenExpired(token)) {
            this.logout();
            return null;
        }

        return token;
    }

    private isTokenExpired(token: string): boolean {
        try {
            const payload = token.split('.')[1];
            const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
            const decoded = atob(base64);
            const parsed = JSON.parse(decoded);
            if (!parsed.exp) return false;
            // exp is in seconds, Date.now() is in ms
            return parsed.exp * 1000 < Date.now();
        } catch {
            return true;
        }
    }

    private getUserFromStorage(): { name: string, email: string, role: string } | null {
        const token = this.getToken();
        if (!token) return null;
        return this.decodeToken(token);
    }

    private decodeToken(token: string): { name: string, email: string, role: string } | null {
        try {
            const payload = token.split('.')[1];
            // Decode Base64Url
            const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
            const decodedIdx = atob(base64);

            // Handle UTF-8 characters via manual percent-encoding
            const bytes = Uint8Array.from(decodedIdx, c => c.charCodeAt(0));
            const decodedUtf8 = new TextDecoder().decode(bytes);

            const parsed = JSON.parse(decodedUtf8);

            return {
                name: parsed.unique_name || 'Usuário',
                email: parsed.email || '',
                role: parsed['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'] || 'Viewer'
            };
        } catch (e) {
            console.error('Error decoding token', e);
            return null;
        }
    }

    hasRole(allowedRoles: string[]): boolean {
        const user = this.currentUser();
        if (!user) return false;
        return allowedRoles.includes(user.role);
    }
}
